import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import * as d3 from 'd3';
import DashboardShell from './DashboardShell';
import StatCard from '../common/StatCard';
import PersonaBadge from '../common/PersonaBadge';
import DataTable from '../common/DataTable';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useTheme } from '../../contexts/ThemeContext';
import { FiPackage, FiLayers, FiActivity } from 'react-icons/fi';
import api from '../../services/api';

interface ManifestRow {
  id: string;
  name: string;
  node_count: number;
  fresh_count: number;
  ghost_count: number;
  stale_count: number;
  hydration_pct: number;
  persona_count: number;
  churn_count: number;
  last_hydrated?: string;
}

const columns: ColumnDef<ManifestRow, any>[] = [
  {
    accessorKey: 'name',
    header: 'Project Name',
    cell: ({ getValue }) => <span className="font-medium">{getValue() as string}</span>,
  },
  { accessorKey: 'node_count', header: 'Nodes', size: 70 },
  {
    accessorKey: 'hydration_pct',
    header: 'Hydration',
    size: 140,
    cell: ({ row }) => {
      const { fresh_count, ghost_count, stale_count, node_count } = row.original;
      const freshPct = node_count > 0 ? (fresh_count / node_count) * 100 : 0;
      const ghostPct = node_count > 0 ? (ghost_count / node_count) * 100 : 0;
      const stalePct = node_count > 0 ? (stale_count / node_count) * 100 : 0;
      return (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden flex">
            <div className="bg-green-500 h-2" style={{ width: `${freshPct}%` }} />
            <div className="bg-amber-500 h-2" style={{ width: `${stalePct}%` }} />
            <div className="bg-gray-600 h-2" style={{ width: `${ghostPct}%` }} />
          </div>
          <span className="text-xs w-10 text-right">{Math.round(freshPct)}%</span>
        </div>
      );
    },
  },
  { accessorKey: 'persona_count', header: 'Personas', size: 80 },
  {
    accessorKey: 'last_hydrated',
    header: 'Last Hydrated',
    cell: ({ getValue }) => {
      const val = getValue() as string | undefined;
      if (!val) return <span className="text-gray-500">{'\u2014'}</span>;
      const diff = Date.now() - new Date(val).getTime();
      const hours = Math.floor(diff / 3600000);
      if (hours < 1) return 'Just now';
      if (hours < 24) return `${hours}h ago`;
      return `${Math.floor(hours / 24)}d ago`;
    },
  },
];

const ArtifactoryDashboard: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { summary } = useAnalytics({ entityType: 'artifactory' });
  const [manifests, setManifests] = useState<ManifestRow[]>([]);
  const [fileTypeCounts, setFileTypeCounts] = useState<Record<string, number>>({});
  const [personaContribs, setPersonaContribs] = useState<{ id: string; name: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/artifactory/projects')
      .then(r => {
        const data = r.data || [];
        // Compute file type counts + persona contributions from all node keys
        const extMap: Record<string, number> = {};
        const personaMap: Record<string, { name: string; count: number }> = {};
        data.forEach((m: any) => {
          const mcNodes = (m.manifest_content || {}).nodes || m.nodes || {};
          Object.values(mcNodes).forEach((node: any) => {
            const filePath = node?.name || node?.path || '';
            if (node?.type === 'directory') return; // skip directories for file type counts
            const ext = filePath.includes('.') ? filePath.split('.').pop()!.toLowerCase() : 'other';
            extMap[ext] = (extMap[ext] || 0) + 1;
            const pid = node?.assigned_persona_id;
            const pname = node?.assigned_persona_name || node?.hydrated_by || '';
            if (pid) {
              if (!personaMap[pid]) personaMap[pid] = { name: pname, count: 0 };
              personaMap[pid].count++;
              if (pname && !personaMap[pid].name) personaMap[pid].name = pname;
            }
          });
        });
        setFileTypeCounts(extMap);
        setPersonaContribs(Object.entries(personaMap).map(([id, v]) => ({ id, name: v.name, count: v.count })));

        setManifests(data.map((m: any) => {
          const mc = m.manifest_content || {};
          const allNodes = Object.values(mc.nodes || m.nodes || {}) as any[];
          const nodes = allNodes.filter((n: any) => n.type !== 'directory'); // files only
          const s = (n: any) => (n.status || '').toLowerCase();
          const fresh = nodes.filter((n: any) => s(n) === 'fresh').length;
          const ghost = nodes.filter((n: any) => s(n) === 'ghost').length;
          const stale = nodes.filter((n: any) => s(n) === 'stale').length;
          return {
            id: m.id,
            name: m.project_name || m.metadata?.project_name || m.name || 'Untitled Blueprint',
            node_count: nodes.length,
            fresh_count: fresh,
            ghost_count: ghost,
            stale_count: stale,
            hydration_pct: nodes.length > 0 ? Math.round((fresh / nodes.length) * 100) : 0,
            persona_count: new Set(nodes.map((n: any) => n.assigned_persona_id).filter(Boolean)).size,
            churn_count: nodes.filter((n: any) => ((n.metadata || {}).hydration_count || 0) >= 3).length,
            last_hydrated: m.updated_at,
          };
        }));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-cyan-600' : 'bg-white border-cyan-500 shadow-sm'
  }`;

  return (
    <DashboardShell title="Blueprint Vault">
      {(dateRange) => (
        <div className="relative">
          <div className="absolute -top-16 -left-16 w-56 h-56 bg-cyan-600/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-40 -right-16 w-40 h-40 bg-blue-600/5 rounded-full blur-3xl pointer-events-none" />

          {/* Stat Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
            <StatCard label="Total Manifests" value={summary?.total ?? manifests.length} icon={<FiPackage className="w-4 h-4" />} accentColor="cyan" />
            <StatCard label="Total Nodes" value={manifests.reduce((s, m) => s + m.node_count, 0)} icon={<FiLayers className="w-4 h-4" />} accentColor="cyan" />
            <StatCard
              label="Hydration Rate"
              value={(() => {
                const total = manifests.reduce((s, m) => s + m.node_count, 0);
                const fresh = manifests.reduce((s, m) => s + m.fresh_count, 0);
                return total > 0 ? `${Math.round((fresh / total) * 100)}%` : '—';
              })()}
              color="#22c55e"
              icon={<FiActivity className="w-4 h-4" />}
              accentColor="cyan"
            />
            <StatCard label="Ghost Nodes" value={manifests.reduce((s, m) => s + m.ghost_count, 0)} color="#6b7280" accentColor="cyan" />
          </div>

          {/* Hydration Donut + Language Breakdown + Persona Distribution */}
          {manifests.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Hydration Donut */}
              <div className={sectionClass}>
                <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400">
                  Hydration Health
                </h2>
                {(() => {
                  const totalN = manifests.reduce((s, m) => s + m.node_count, 0);
                  const freshN = manifests.reduce((s, m) => s + m.fresh_count, 0);
                  const ghostN = manifests.reduce((s, m) => s + m.ghost_count, 0);
                  const staleN = manifests.reduce((s, m) => s + m.stale_count, 0);
                  const otherN = totalN - freshN - ghostN - staleN;
                  const pct = totalN > 0 ? Math.round((freshN / totalN) * 100) : 0;
                  return (
                    <div className="flex flex-col items-center">
                      <div className="relative w-32 h-32">
                        <svg viewBox="0 0 36 36" className="w-32 h-32">
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke={darkMode ? '#1f2937' : '#e5e7eb'} strokeWidth="3" />
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#22c55e" strokeWidth="3"
                            strokeDasharray={`${pct} ${100-pct}`} strokeDashoffset="25" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className={`text-2xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{pct}%</span>
                          <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>FRESH</span>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-3 text-xs">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />{freshN}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-600" />{ghostN}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />{staleN}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Language Breakdown */}
              <div className={sectionClass}>
                <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
                  File Types
                </h2>
                {Object.keys(fileTypeCounts).length > 0 ? (
                  <div className="space-y-1.5">
                    {Object.entries(fileTypeCounts)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 10)
                      .map(([ext, count]) => {
                        const max = Math.max(...Object.values(fileTypeCounts));
                        const pct = max > 0 ? (count / max) * 100 : 0;
                        return (
                          <div key={ext} className="flex items-center gap-2">
                            <span className={`text-xs w-12 text-right font-mono ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>.{ext}</span>
                            <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                              <div className="h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className={`text-xs w-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{count}</span>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No file type data yet
                  </p>
                )}
              </div>

              {/* Persona Distribution */}
              <div className={sectionClass}>
                <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-orange-400">
                  Top Contributors
                </h2>
                {personaContribs.length > 0 ? (
                  <div className="space-y-1.5">
                    {personaContribs
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 8)
                      .map(p => {
                        const max = Math.max(...personaContribs.map(x => x.count));
                        const pct = max > 0 ? (p.count / max) * 100 : 0;
                        const totalNodes = personaContribs.reduce((s, x) => s + x.count, 0);
                        const share = totalNodes > 0 ? Math.round((p.count / totalNodes) * 100) : 0;
                        return (
                          <div key={p.id} className="flex items-center gap-2">
                            <PersonaBadge personaId={p.id} personaName={p.name || 'Unknown'} size="xs" clickable={false} />
                            <span className={`text-xs truncate min-w-[80px] flex-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {(p.name || 'Unknown').replace(/_/g, ' ')}
                            </span>
                            <div className="w-20 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                              <div className="h-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className={`text-xs w-14 text-right tabular-nums ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {p.count} ({share}%)
                            </span>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No persona contributions yet
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Manifest Table */}
          <div className={sectionClass}>
            <h2 className="text-lg font-semibold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400">
              All Projects
            </h2>
            <DataTable
              columns={columns}
              data={manifests}
              isLoading={loading}
              searchable
              searchPlaceholder="Search projects..."
              pageSize={20}
              emptyMessage="No artifactory projects found"
              onRowClick={(row) => navigate(`/dashboards/artifactory/${row.id}`)}
              expandableRow={(row) => (
                <div className={`flex items-center justify-between ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-green-400">FRESH: {row.fresh_count}</span>
                    <span className="text-gray-400">GHOST: {row.ghost_count}</span>
                    <span className="text-amber-400">STALE: {row.stale_count}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/dashboards/artifactory/${row.id}`); }}
                    className="px-3 py-1.5 text-xs rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-700 hover:to-blue-700 transition-all"
                  >
                    Open Full View <span className="inline-block transition-transform group-hover:translate-x-1">&rarr;</span>
                  </button>
                </div>
              )}
            />
          </div>
        </div>
      )}
    </DashboardShell>
  );
};

export default ArtifactoryDashboard;
